import React, { useEffect, useRef, useState } from 'react';
import once from 'lodash/once';
import { Body, Header, Modal } from '@organisms/modals/base';
import ProgramSurveyQuestions, { InnerRefType } from './ProgramSurveyQuestions';
import ProgramSurveyThankyou from './ProgramSurveyThankyou';
import { Box } from '@components/layout/Grid';
import {
  ActionInput,
  ProgramCategory,
  ProgramSurveyElementFieldsFragment,
  ProxyEventKind,
  useAnswerSurveyMutation,
  useEventProxyMutation,
} from '@gql/generated';
import { snackbarVar } from '@lib/apollo/policies';
import { t } from '@lingui/macro';
import AnalyticsManager from '@lib/analytics/manager';

type Props = {
  surveyElement: ProgramSurveyElementFieldsFragment;
  programCategory: ProgramCategory;
  open: boolean;
  onClose(): void;
  refetchProgram(): void;
  programSlug: string;
  programTitle: string;
};

const SurveyElementModal = ({
  surveyElement,
  programCategory,
  open,
  onClose,
  refetchProgram,
  programSlug,
  programTitle,
}: Props) => {
  const ref = useRef<InnerRefType>();
  const [submitted, setSubmitted] = useState(false);
  const [sendStartSurveyEvent] = useEventProxyMutation();
  const [loading, setLoading] = useState(false);
  const [completeSurvey, { data }] = useAnswerSurveyMutation();

  const sendStartEvent = once(() => {
    AnalyticsManager().sendSurveyStartedEvent({
      title: surveyElement.title,
      part_slug: surveyElement.slug,
      program_slug: programSlug,
      program_title: programTitle,
    });
    sendStartSurveyEvent({
      variables: { event: ProxyEventKind['SurveyStarted'], properties: [] },
    });
  });

  const sendCompleteEvent = once(() => {
    AnalyticsManager().sendSurveyCompletedEvent({
      title: surveyElement.title,
      part_slug: surveyElement.slug,
      program_slug: programSlug,
      program_title: programTitle,
    });
  });

  useEffect(() => {
    if (open) {
      sendStartEvent();
    } else {
      setTimeout(() => {
        setSubmitted(false);
      }, 300);
    }
  }, [open]);

  const handleClose = async () => {
    if (
      ref.current &&
      surveyElement?.surveysAnswer?.state !== 'finished' &&
      !data
    ) {
      const values = ref.current?.getValues?.();
      const answers = Object.entries(values).map(([id, values]) => ({
        id,
        values,
      }));
      await completeSurvey({
        variables: {
          answers,
          surveyPartId: surveyElement?.id,
          surveyAnswerId: null,
          surveyAction: ActionInput['Draft'],
        },
      });
      refetchProgram();
      snackbarVar({
        isSnackBarOpen: true,
        snackBarMessage: t({
          id: 'programs_2022.survey.draft_saved',
        }),
      });
    } else if (surveyElement?.surveysAnswer?.state === 'finished') {
      const values = ref.current?.getValues?.();
      const intialValues = surveyElement?.surveysAnswer?.content?.reduce?.(
        (acc, answer) => {
          acc[answer.key] = [...answer.value];
          return acc;
        },
        {}
      );

      // we display alert if user wants to leave without saving
      if (
        values &&
        Object.keys(values).some(key => {
          return values[key]?.join(',') !== intialValues[key].join(',');
        }) &&
        !submitted
      ) {
        if (!confirm(t`programs_2022.survey.exit_alert`)) {
          return;
        }
      }
    }
    onClose();
  };

  const handleSubmit = async (values: Record<string, string[]>) => {
    setLoading(true);
    const answers = Object.entries(values).map(([id, values]) => ({
      id,
      values,
    }));
    try {
      sendCompleteEvent();
      const { data } = await completeSurvey({
        variables: {
          answers,
          surveyPartId: surveyElement?.id,
          surveyAnswerId: surveyElement?.surveysAnswer?.id || null,
          surveyAction: ActionInput['Complete'],
        },
      });
      if (data?.answerSurvey?.errors?.length == 0) {
        refetchProgram();
        setSubmitted(true);
      }
    } catch (ex) {
      //
    }
    setLoading(false);
  };

  return (
    <Modal open={open} handleClose={handleClose}>
      <Header handleClose={handleClose}></Header>
      <Body px={0} pb={50}>
        {submitted ? (
          <ProgramSurveyThankyou
            survey={surveyElement.survey}
            programCategory={programCategory}
            closeModal={handleClose}
          />
        ) : (
          <Box mt={-30}>
            <ProgramSurveyQuestions
              innerRef={ref}
              loading={loading}
              survey={surveyElement.survey}
              surveysAnswer={surveyElement.surveysAnswer}
              programCategory={programCategory}
              onSubmit={handleSubmit}
            />
          </Box>
        )}
      </Body>
    </Modal>
  );
};

export default SurveyElementModal;
